import React, { useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import { useTranslation } from "react-i18next";
import { contactApi } from "../api/contactApi";
import ReCAPTCHA from "react-google-recaptcha";

export default function Contact() {
  const { t } = useTranslation();

  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const [ok, setOk] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  // ✅ reCAPTCHA
  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");

  // ✅ SITE KEY (Vite və CRA dəstəyi)
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;


  const waNumber = "994602113435"; // öz WhatsApp nömrəni yaz
  const waLink = useMemo(() => {
    const text =
      t("contact_btn_whatsapp") === "Write on WhatsApp"
        ? "Hello! I'm writing about product and pricing."
        : t("contact_btn_whatsapp") === "Написать в WhatsApp"
        ? "Здравствуйте! Пишу по поводу продукта и цены."
        : "Salam! Məhsul və qiymət sorğusu üçün yazıram.";
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`;
  }, [waNumber, t]);

  const onChange = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return t("contact_err_name");
    if (!form.email.trim()) return t("contact_err_email");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim()))
      return t("contact_err_email_valid");
    if (!form.message.trim()) return t("contact_err_message");

    // ✅ Captcha yoxla
    if (!captchaToken) return t("contact_err_captcha");

    // ✅ Site key yoxla (env səhvdirsə)
    if (!RECAPTCHA_SITE_KEY) return "reCAPTCHA site key tapılmadı (.env yoxla)";

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);

    const v = validate();
    if (v) {
      setErr(v);
      return;
    }

    setSending(true);
    try {
      await contactApi.create({
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        captchaToken, // ✅ backend-ə göndər
      });

      setOk(true);
      setForm({ name: "", email: "", message: "" });

      // ✅ captcha reset
      setCaptchaToken("");
      recaptchaRef.current?.reset();

      window.clearTimeout(window.__contactOkTimer);
      window.__contactOkTimer = window.setTimeout(() => setOk(false), 2500);
    } catch (e2) {
      const msg = e2?.response?.data || e2?.message || t("contact_err_send_fail");
      setErr(String(msg));
      setOk(false);

      // ✅ error zamanı da reset etmək olar
      setCaptchaToken("");
      recaptchaRef.current?.reset();
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <section className="py-4 py-md-5">
        <div className="container">
          {/* Header */}
          <div className="mb-4">
            <h1 className="fw-bold mb-2" style={{ color: "#3a4858" }}>
              {t("contact_title")}
            </h1>
            <p className="text-secondary mb-0">{t("contact_subtitle")}</p>
          </div>

          <div className="row g-4">
            {/* Form */}
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5
                      className="card-title mb-0 fw-bold"
                      style={{ color: "#3a4858" }}
                    >
                      {t("contact_send_title")}
                    </h5>
                  </div>

                  {/* Success */}
                  {ok && (
                    <div
                      className="alert alert-success d-flex align-items-center"
                      role="alert"
                      style={{ marginBottom: 16 }}
                    >
                      <span className="me-2">✅</span>
                      <div>{t("contact_sent_ok")}</div>
                    </div>
                  )}

                  {/* Error */}
                  {err && (
                    <div
                      className="alert alert-danger d-flex align-items-center"
                      role="alert"
                      style={{ marginBottom: 16 }}
                    >
                      <span className="me-2">⚠️</span>
                      <div>{err}</div>
                    </div>
                  )}

                  <form onSubmit={onSubmit} className="needs-validation" noValidate>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        {t("contact_label_name")}
                      </label>
                      <input
                        required
                        value={form.name}
                        onChange={onChange("name")}
                        className="form-control form-control-lg"
                        disabled={sending}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        {t("contact_label_email")}
                      </label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        className="form-control form-control-lg"
                        disabled={sending}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        {t("contact_label_message")}
                      </label>
                      <textarea
                        required
                        value={form.message}
                        onChange={onChange("message")}
                        className="form-control"
                        rows={6}
                        style={{ resize: "vertical" }}
                        disabled={sending}
                      />
                    </div>

                    {/* ✅ reCAPTCHA */}
                    <div className="mb-3">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY || ""}
                        onChange={(token) => setCaptchaToken(token || "")}
                        onExpired={() => setCaptchaToken("")}
                      />
                    </div>

                    <div className="d-grid d-sm-flex gap-2">
                      <button
                        type="submit"
                        className="btn btn-lg text-white"
                        style={{ background: "#3a4858" }}
                        disabled={sending || !captchaToken}
                      >
                        {sending ? t("contact_btn_sending") : t("contact_btn_send")}
                      </button>

                      <a
                        className="btn btn-lg btn-outline-secondary"
                        href={waLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("contact_btn_whatsapp")}
                      </a>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body p-4">
                  <div className="ratio ratio-16x9 rounded-4 overflow-hidden border">
                    <iframe
                      title="map"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src="https://www.google.com/maps?q=Nax%C3%A7%C4%B1van%20Azerbaijan&output=embed"
                      style={{ border: 0 }}
                      allowFullScreen=""
                    />
                  </div>

                  <div
                    className="mt-3 p-3 rounded-4"
                    style={{ background: "rgba(58,72,88,.06)" }}
                  >
                    <div className="fw-semibold mb-1" style={{ color: "#3a4858" }}>
                      {t("contact_info_title")}
                    </div>
                    <div className="text-secondary">
                      <div>
                        <span className="fw-semibold text-dark">
                          {t("contact_phone")}
                        </span>{" "}
                        +994 60 211 34 35
                      </div>
                      <div>
                        <span className="fw-semibold text-dark">
                          {t("contact_address")}
                        </span>{" "}
                        Naxçıvan, Azərbaycan
                      </div>
                      <div>
                        <span className="fw-semibold text-dark">
                          {t("contact_email")}
                        </span>{" "}
                        info@scoriva.net
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /Map */}
          </div>
        </div>
      </section>
    </Layout>
  );
}
